import { useCallback, useEffect, useState } from "react";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import api from "../../services/api";
import { formatRelativeTime } from "../../utils/formatters";
import styles from "./AttentionHistoryModal.module.css";

// Absolute timestamp in the viewer's locale. The API sends UTC ISO strings
// (Task 13) — localizing for display is the UI's job. Mirrors the attention-row
// convention already used on TasksPage.
const formatAbsolute = (value) =>
  value ? new Date(value).toLocaleString() : "—";

const ACTION_LABELS = {
  handled: "Handled",
  unhandled: "Undone",
};

// Chronological attention trail (handled / undone) for one ticket. Reads
// GET /admin/tasks/tickets/:id/attention-history (Task 13, gated
// tasks.history_view) which returns events ascending; the trail survives the
// 24h auto-archival, so history stays retrievable after a ticket leaves the list.
const AttentionHistoryModal = ({ ticket, onClose }) => {
  const isOpen = Boolean(ticket);
  const ticketId = ticket?.id ?? null;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/admin/tasks/tickets/${ticketId}/attention-history`
      );
      setEvents(response?.events ?? []);
    } catch (err) {
      setError(err.message || "Failed to load attention history");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (!isOpen) {
      setEvents([]);
      setError(null);
      return;
    }
    loadHistory();
  }, [isOpen, loadHistory]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        ticket
          ? `Attention history · #${ticket.ticketNumber}`
          : "Attention history"
      }
      size="medium"
    >
      {loading ? (
        <div className={styles.center}>
          <LoadingSpinner size="small" />
        </div>
      ) : error ? (
        <div className={styles.errorBox}>
          {error}{" "}
          <button
            type="button"
            className={styles.retryBtn}
            onClick={loadHistory}
          >
            Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className={styles.empty}>No attention history yet</div>
      ) : (
        <ol className={styles.timeline}>
          {events.map((event) => (
            <li key={event.id} className={styles.event}>
              <span
                className={`${styles.dot} ${event.action === "handled" ? styles.dotHandled : styles.dotUndone}`}
                aria-hidden="true"
              />
              <div className={styles.eventBody}>
                <span className={styles.eventLabel}>
                  {ACTION_LABELS[event.action] || event.action}
                  {event.actorDisplayName ? ` by ${event.actorDisplayName}` : ""}
                </span>
                <span
                  className={styles.eventTime}
                  title={formatAbsolute(event.createdAt)}
                >
                  {formatAbsolute(event.createdAt)} ·{" "}
                  {formatRelativeTime(event.createdAt)}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
};

export default AttentionHistoryModal;
